/* ============================================================================
   <custom-product-gallery>
     - Click thumbnail → activate matching main media slide
     - Pause any playing video when switching slides
     - Keyboard: Left/Right arrows on the thumb rail navigate

   <product-form>
     - Variant radio change → update hidden input "id" + button label/state
   ============================================================================ */
(function () {
  'use strict';

  /* ---------- Gallery ---------- */
  if (!customElements.get('custom-product-gallery')) {
    class CustomProductGallery extends HTMLElement {
      constructor() {
        super();
        this.onThumbClick = this.onThumbClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
      }

      connectedCallback() {
        this.thumbs = Array.from(this.querySelectorAll('[data-mpc-thumb]'));
        this.slides = Array.from(this.querySelectorAll('[data-mpc-slide]'));

        this.thumbs.forEach((t) => t.addEventListener('click', this.onThumbClick));
        this.addEventListener('keydown', this.onKeyDown);
      }

      onThumbClick(e) {
        const thumb = e.currentTarget;
        const id = thumb.dataset.mediaId;
        if (!id) return;
        this.activate(id);
      }

      onKeyDown(e) {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        const focused = document.activeElement;
        if (!focused || !this.thumbs.includes(focused)) return;
        e.preventDefault();
        const i = this.thumbs.indexOf(focused);
        const next = e.key === 'ArrowRight'
          ? (i + 1) % this.thumbs.length
          : (i - 1 + this.thumbs.length) % this.thumbs.length;
        this.thumbs[next].focus();
        this.thumbs[next].click();
      }

      activate(mediaId) {
        // Pause any currently-playing video on the previously active slide
        const playingVideo = this.querySelector('.mpc-gallery__slide--active video');
        if (playingVideo) {
          try { playingVideo.pause(); } catch (e) { /* noop */ }
        }

        // Update slides
        this.slides.forEach((slide) => {
          const match = slide.dataset.mediaId === mediaId;
          slide.classList.toggle('mpc-gallery__slide--active', match);
          if (match) slide.removeAttribute('hidden');
          else slide.setAttribute('hidden', '');
        });

        // Update thumbs
        this.thumbs.forEach((thumb) => {
          const match = thumb.dataset.mediaId === mediaId;
          thumb.classList.toggle('mpc-gallery__thumb--active', match);
          thumb.setAttribute('aria-selected', match ? 'true' : 'false');
          if (match) {
            // scroll into view in the rail (vertical or horizontal)
            try {
              thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
            } catch (e) { /* noop */ }
          }
        });
      }
    }

    customElements.define('custom-product-gallery', CustomProductGallery);
  }

  /* ---------- Product form (variant resolution) ---------- */
  if (!customElements.get('product-form')) {
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.variants = null;
        this.onOptionChange = this.onOptionChange.bind(this);
      }

      connectedCallback() {
        const dataEl = this.querySelector('[data-mpc-variants]');
        if (dataEl) {
          try { this.variants = JSON.parse(dataEl.textContent); } catch (e) { /* noop */ }
        }

        this.querySelectorAll('[data-mpc-option-input]').forEach((input) => {
          input.addEventListener('change', this.onOptionChange);
        });
      }

      onOptionChange() {
        if (!this.variants) return;

        // Read the currently selected option values
        const selectedValues = Array.from(this.querySelectorAll('[data-mpc-option-input]:checked'))
          .map((i) => i.value);

        // Find the matching variant
        const variant = this.variants.find((v) => {
          if (!v.options) return false;
          return v.options.every((optVal, idx) => optVal === selectedValues[idx]);
        });

        const idInput = this.querySelector('[data-mpc-variant-id]');
        const cta = this.querySelector('[data-mpc-cta]');
        const ctaText = this.querySelector('[data-mpc-cta-text]');

        if (!variant) {
          if (cta) {
            cta.disabled = true;
            cta.setAttribute('aria-disabled', 'true');
          }
          if (ctaText) ctaText.textContent = 'Unavailable';
          return;
        }

        if (idInput) idInput.value = variant.id;

        if (variant.available) {
          if (cta) {
            cta.disabled = false;
            cta.removeAttribute('aria-disabled');
          }
          if (ctaText) ctaText.textContent = 'Add to cart';
        } else {
          if (cta) {
            cta.disabled = true;
            cta.setAttribute('aria-disabled', 'true');
          }
          if (ctaText) ctaText.textContent = 'Sold out';
        }

        // If the variant has a specific featured image, jump the gallery to it
        if (variant.featured_image && variant.featured_image.id) {
          const gallery = document.querySelector('custom-product-gallery');
          if (gallery && typeof gallery.activate === 'function') {
            gallery.activate(String(variant.featured_image.id));
          }
        }
      }
    }

    customElements.define('product-form', ProductForm);
  }
})();
